/**
 * SalesTrendChart — Monthly sales trend with expand/modal support.
 *
 * Compact view: top 12 months (full FY), line chart.
 * Expanded view: full dataset, switchable chart type + all toolbar controls.
 */
import { useMemo, useCallback } from 'react';
import {
  LineChart, Line,
  BarChart, Bar,
  AreaChart, Area,
  XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import ChartContainer from './ChartContainer';
import ChartModal from '../ChartModal/ChartModal';
import useChartExpand from '../../hooks/useChartExpand';
import './SalesTrendChart.css';

// ── Colours ───────────────────────────────────────────────────────────────────
const C = {
  target:   '#e53935',
  cy_sales: '#43a047',
  ly_sales: '#1e88e5',
};

// ── Tooltip ───────────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const fmt = (v) => `₹${(v / 100000).toFixed(2)}L`;
  const byKey  = Object.fromEntries(payload.map(p => [p.dataKey, p.value]));
  const achPct = byKey.cy_sales != null && byKey.target > 0
    ? ((byKey.cy_sales / byKey.target) * 100).toFixed(1)
    : null;
  const order  = ['target', 'cy_sales', 'ly_sales'];
  const sorted = [...payload].sort((a, b) => order.indexOf(a.dataKey) - order.indexOf(b.dataKey));

  return (
    <div className="trend-tooltip">
      <p className="trend-tooltip__title">{label}</p>
      {sorted.map(p => (
        <p key={p.dataKey} className="trend-tooltip__row" style={{ color: C[p.dataKey] ?? p.color }}>
          {p.name}: <strong>{fmt(p.value)}</strong>
        </p>
      ))}
      {achPct != null && (
        <p className="trend-tooltip__row trend-tooltip__ach">
          Achievement: <strong>{achPct}%</strong>
        </p>
      )}
    </div>
  );
};

// ── Chart renderer (shared between compact + modal) ───────────────────────────
const TrendChartInner = ({ data, chartType = 'line', height = 260 }) => {
  const fmt = (v) => `${(v / 100000).toFixed(0)}L`;

  const commonAxis = (w) => ({
    xAxis: (
      <XAxis
        dataKey="month"
        tick={{ fontSize: 11, fill: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}
        axisLine={false} tickLine={false}
      />
    ),
    yAxis: (
      <YAxis
        tick={{ fontSize: 11, fill: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}
        axisLine={false} tickLine={false}
        width={60} tickFormatter={fmt}
      />
    ),
    grid:    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />,
    tooltip: <Tooltip content={<CustomTooltip />} />,
    legend:  <Legend wrapperStyle={{ fontSize: 12, fontFamily: 'var(--font-body)' }} />,
  });

  const lines = (type) => {
    if (type === 'area') {
      return (
        <>
          <Area type="monotone" dataKey="target"   name="Target"   stroke={C.target}   fill={C.target}   fillOpacity={0.08} strokeWidth={2} strokeDasharray="5 5" dot={false} />
          <Area type="monotone" dataKey="ly_sales" name="LY Sales" stroke={C.ly_sales} fill={C.ly_sales} fillOpacity={0.1}  strokeWidth={2} dot={false} />
          <Area type="monotone" dataKey="cy_sales" name="CY Sales" stroke={C.cy_sales} fill={C.cy_sales} fillOpacity={0.15} strokeWidth={2.5} dot={{ r: 3, fill: C.cy_sales }} activeDot={{ r: 5 }} />
        </>
      );
    }
    if (type === 'bar') {
      return (
        <>
          <Bar dataKey="target"   name="Target"   fill={C.target}   radius={[3,3,0,0]} maxBarSize={18} />
          <Bar dataKey="ly_sales" name="LY Sales" fill={C.ly_sales} radius={[3,3,0,0]} maxBarSize={18} />
          <Bar dataKey="cy_sales" name="CY Sales" fill={C.cy_sales} radius={[3,3,0,0]} maxBarSize={18} />
        </>
      );
    }
    if (type === 'hbar') {
      return (
        <>
          <Bar dataKey="target"   name="Target"   fill={C.target}   radius={[0,3,3,0]} maxBarSize={14} />
          <Bar dataKey="ly_sales" name="LY Sales" fill={C.ly_sales} radius={[0,3,3,0]} maxBarSize={14} />
          <Bar dataKey="cy_sales" name="CY Sales" fill={C.cy_sales} radius={[0,3,3,0]} maxBarSize={14} />
        </>
      );
    }
    // default: line
    return (
      <>
        <Line type="monotone" dataKey="target"   name="Target"   stroke={C.target}   strokeWidth={2}   strokeDasharray="5 5" dot={false} />
        <Line type="monotone" dataKey="ly_sales" name="LY Sales" stroke={C.ly_sales} strokeWidth={2}   dot={false} />
        <Line type="monotone" dataKey="cy_sales" name="CY Sales" stroke={C.cy_sales} strokeWidth={2.5} dot={{ r: 3, fill: C.cy_sales }} activeDot={{ r: 5 }} />
      </>
    );
  };

  const h = chartType === 'hbar' ? Math.max(height, data.length * 36) : height;

  return (
    <ChartContainer height={h}>
      {(w, h) => {
        const ax = commonAxis(w);
        if (chartType === 'hbar') {
          return (
            <BarChart width={w} height={h} data={data} layout="vertical" margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
              {ax.grid}
              <XAxis type="number" tickFormatter={fmt} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="month" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} width={80} />
              {ax.tooltip}
              {ax.legend}
              {lines('hbar')}
            </BarChart>
          );
        }
        if (chartType === 'bar') {
          return (
            <BarChart width={w} height={h} data={data} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
              {ax.grid}
              {ax.xAxis}
              {ax.yAxis}
              {ax.tooltip}
              {ax.legend}
              {lines('bar')}
            </BarChart>
          );
        }
        if (chartType === 'area') {
          return (
            <AreaChart width={w} height={h} data={data} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
              {ax.grid}
              {ax.xAxis}
              {ax.yAxis}
              {ax.tooltip}
              {ax.legend}
              {lines('area')}
            </AreaChart>
          );
        }
        // line (default)
        return (
          <LineChart width={w} height={h} data={data} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
            {ax.grid}
            {ax.xAxis}
            {ax.yAxis}
            {ax.tooltip}
            {ax.legend}
            {lines('line')}
          </LineChart>
        );
      }}
    </ChartContainer>
  );
};

// ── Main export ───────────────────────────────────────────────────────────────
const SalesTrendChart = ({ data, loading }) => {
  const { isOpen, open, close } = useChartExpand();

  // Compact view: show all months (FY = 12 months max)
  const compactData = useMemo(() => data?.slice(0, 12) ?? [], [data]);

  // Modal stats
  const stats = useMemo(() => {
    if (!data?.length) return [];
    const totalCy     = data.reduce((s, r) => s + (r.cy_sales ?? 0), 0);
    const totalTarget = data.reduce((s, r) => s + (r.target   ?? 0), 0);
    const totalLy     = data.reduce((s, r) => s + (r.ly_sales ?? 0), 0);
    const ach  = totalTarget > 0 ? ((totalCy / totalTarget) * 100).toFixed(1) : '—';
    const grth = totalLy     > 0 ? (((totalCy - totalLy) / totalLy) * 100).toFixed(1) : '—';
    return [
      { label: 'Total CY Sales', value: `₹${(totalCy / 100000).toFixed(1)}L` },
      { label: 'Total Target',   value: `₹${(totalTarget / 100000).toFixed(1)}L` },
      { label: 'Total LY Sales', value: `₹${(totalLy / 100000).toFixed(1)}L` },
      { label: 'Achievement',    value: `${ach}%`,  color: Number(ach) >= 100 ? 'var(--green)' : Number(ach) >= 85 ? 'var(--orange)' : 'var(--red)' },
      { label: 'Growth',         value: `${grth}%`, color: Number(grth) >= 0 ? 'var(--green)' : 'var(--red)' },
    ];
  }, [data]);

  if (loading) return <div className="trend-chart"><div className="chart-skeleton" /></div>;
  if (!data?.length) return <div className="chart-empty">No trend data available</div>;

  return (
    <>
      {/* ── Compact chart ── */}
      <div className="trend-chart">
        <div className="trend-chart__header">
          <button className="expand-btn" onClick={open} title="Expand chart">⤢</button>
        </div>
        <TrendChartInner data={compactData} chartType="line" height={260} />
      </div>

      {/* ── Expanded modal ── */}
      <ChartModal
        open={isOpen}
        onClose={close}
        title="Sales Trend"
        data={data}
        labelKey="month"
        valueKey="cy_sales"
        supportedTypes={['line', 'bar', 'area', 'hbar']}
        defaultType="line"
        stats={stats}
        exportFilename="sales-trend"
      >
        {(chartType, processedData) => (
          <TrendChartInner
            data={processedData}
            chartType={chartType}
            height={420}
          />
        )}
      </ChartModal>
    </>
  );
};

export default SalesTrendChart;
